/*
 * Stepper shows progress through steps; Wizard runs a multi-step flow with the current step in the URL
 * (`?step=<id>`), so Back/Forward and links work and a refresh keeps the place.
 *
 *   <Wizard
 *     id="add-server"                                     // remembers how far the person got (session)
 *     steps={[
 *       { id: 'details', title: t('…'), content: <Details form={form} />, onNext: () => form.trigger() },
 *       { id: 'plan', title: t('…'), content: <PlanStep /> },
 *       { id: 'install', title: t('…'), content: <Install />, nextLabel: t('…') },
 *     ]}
 *     onFinish={() => navigate(`/servers/${id}`)}
 *   />
 *
 * `onNext` may return false (or a promise of false) to stay on the step. `useWizard(ids)` gives the same URL
 * state to custom layouts.
 */
import { ArrowLeft, Check } from '@phosphor-icons/react'
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { cn } from '@/lib/cn'
import { Button } from './Button'

export type StepState = 'done' | 'current' | 'upcoming'
export type StepperStep = { id: string; title: ReactNode; description?: ReactNode }

export function Stepper({
  steps,
  current,
  orientation = 'horizontal',
  onStepClick,
  className,
}: {
  steps: StepperStep[]
  current: string
  orientation?: 'horizontal' | 'vertical'
  /** only completed steps are clickable */
  onStepClick?: (id: string) => void
  className?: string
}) {
  const { t } = useTranslation('common')
  const index = Math.max(
    0,
    steps.findIndex((s) => s.id === current),
  )
  const state = (i: number): StepState => (i < index ? 'done' : i === index ? 'current' : 'upcoming')
  return (
    <ol
      aria-label={t('ui.stepper.label', { n: index + 1, total: steps.length })}
      className={cn(
        orientation === 'horizontal' ? 'flex items-start gap-2' : 'flex flex-col gap-0',
        className,
      )}
    >
      {steps.map((s, i) => {
        const st = state(i)
        const clickable = onStepClick && st === 'done'
        const mark = (
          <span
            className={cn(
              'grid size-7 shrink-0 place-items-center rounded-full figures text-12 font-semibold transition-colors',
              st === 'done' && 'bg-brand-600 text-on-brand',
              st === 'current' && 'bg-surface text-brand-700 ring-2 ring-brand-600',
              st === 'upcoming' && 'bg-surface text-subtle ring-1 ring-hairline-strong',
            )}
          >
            {st === 'done' ? <Check className="size-3.5" weight="bold" aria-hidden="true" /> : i + 1}
          </span>
        )
        const text = (
          <span className={cn('flex min-w-0 flex-col', orientation === 'horizontal' && 'max-sm:sr-only')}>
            <span className={cn('text-13 font-semibold', st === 'upcoming' ? 'text-subtle' : 'text-ink')}>
              {s.title}
            </span>
            {s.description && orientation === 'vertical' ? (
              <span className="text-12 text-subtle">{s.description}</span>
            ) : null}
          </span>
        )
        const inner = (
          <span className="flex items-center gap-2.5">
            {mark}
            {text}
          </span>
        )
        return (
          <li
            key={s.id}
            aria-current={st === 'current' ? 'step' : undefined}
            className={cn(
              'relative',
              orientation === 'horizontal'
                ? 'flex flex-1 items-center gap-2 last:flex-none'
                : 'pb-6 last:pb-0',
            )}
          >
            {clickable ? (
              <button type="button" onClick={() => onStepClick(s.id)} className="rounded-full text-start">
                {inner}
              </button>
            ) : (
              inner
            )}
            {i < steps.length - 1 ? (
              orientation === 'horizontal' ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-0.5 min-w-4 flex-1 rounded-full',
                    i < index ? 'bg-brand-600' : 'bg-hairline-strong',
                  )}
                />
              ) : (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute start-3.5 top-8 bottom-1 w-0.5 -translate-x-1/2 rounded-full rtl:translate-x-1/2',
                    i < index ? 'bg-brand-600' : 'bg-hairline',
                  )}
                />
              )
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

/** URL-backed step state: `?<param>=<id>`. */
export function useWizard(ids: string[], { param = 'step' }: { param?: string } = {}) {
  const [params, setParams] = useSearchParams()
  const raw = params.get(param)
  const current = raw && ids.includes(raw) ? raw : ids[0]!
  const index = ids.indexOf(current)
  const goTo = (id: string, opts: { replace?: boolean } = {}) => {
    const next = new URLSearchParams(params)
    next.set(param, id)
    setParams(next, { replace: opts.replace })
  }
  return {
    current,
    index,
    isFirst: index === 0,
    isLast: index === ids.length - 1,
    goTo,
    next: () => index < ids.length - 1 && goTo(ids[index + 1]!),
    back: () => index > 0 && goTo(ids[index - 1]!),
  }
}

export type WizardStep = StepperStep & {
  content: ReactNode
  /** validate or save before moving on; return false to stay */
  onNext?: () => boolean | void | Promise<boolean | void>
  nextLabel?: ReactNode
  /** hide the default footer (the step renders its own actions) */
  hideFooter?: boolean
  canGoBack?: boolean
}

export function Wizard({
  id,
  steps,
  onFinish,
  finishLabel,
  param = 'step',
  className,
  aside,
}: {
  id: string
  steps: WizardStep[]
  onFinish?: () => void | Promise<void>
  finishLabel?: ReactNode
  param?: string
  className?: string
  /** content beside the steps on wide screens (a summary) */
  aside?: ReactNode
}) {
  const { t } = useTranslation('common')
  const w = useWizard(
    steps.map((s) => s.id),
    { param },
  )
  const key = `bpanel.wizard.${id}`
  const [reached, setReached] = useState(() => Number(sessionStorage.getItem(key) ?? 0))
  const [busy, setBusy] = useState(false)
  const step = steps[w.index]!

  // never land past the furthest step reached (e.g. a pasted link to the last step)
  useEffect(() => {
    if (w.index > reached) w.goTo(steps[reached]?.id ?? steps[0]!.id, { replace: true })
  }, [w, reached, steps])

  const next = async () => {
    setBusy(true)
    try {
      const ok = await step.onNext?.()
      if (ok === false) return
      if (w.isLast) {
        sessionStorage.removeItem(key)
        await onFinish?.()
        return
      }
      const r = Math.max(reached, w.index + 1)
      setReached(r)
      sessionStorage.setItem(key, String(r))
      w.next()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <Stepper steps={steps} current={w.current} onStepClick={(sid) => w.goTo(sid)} />
      <div className={cn('grid gap-6', aside && 'lg:grid-cols-[minmax(0,1fr)_20rem]')}>
        <div className="flex min-w-0 flex-col gap-6">
          <div key={step.id} className="animate-fade-in">
            {step.content}
          </div>
          {!step.hideFooter ? (
            <div className="flex flex-col-reverse gap-2 border-t border-hairline pt-5 sm:flex-row sm:items-center sm:justify-between">
              {!w.isFirst && step.canGoBack !== false ? (
                <Button variant="ghost" onClick={w.back} disabled={busy}>
                  {t('actions.back')}
                </Button>
              ) : (
                <span />
              )}
              <Button
                onClick={next}
                loading={busy}
                iconEnd={<ArrowLeft className="flip-ltr" aria-hidden="true" />}
              >
                {step.nextLabel ?? (w.isLast ? (finishLabel ?? t('actions.finish')) : t('actions.continue'))}
              </Button>
            </div>
          ) : null}
        </div>
        {aside ? <aside className="flex flex-col gap-4">{aside}</aside> : null}
      </div>
    </div>
  )
}
